import { TableCell, TableSortLabel } from '@mui/material';
import type { TableCellProps } from '@mui/material';

export interface SortableHeaderCellProps<T extends string> {
  /** The column key this header represents (matches a value in the page's `SortableColumn` literal-union). */
  column: T;
  /** The currently sorted column from URL state. */
  current: T;
  /** The currently sorted direction from URL state. */
  direction: 'asc' | 'desc';
  /** Click handler — receives the clicked `column`. Caller is responsible for the dir-toggle (use `nextSortDir` from `@/shared`). */
  onSort: (column: T) => void;
  /** The translated header label (typically `t('X.column')`). */
  children: React.ReactNode;
  /** Optional `sx` passthrough for column-specific width / padding tweaks. */
  sx?: TableCellProps['sx'];
  /** Optional alignment passthrough. */
  align?: TableCellProps['align'];
  /**
   * Optional predicate for compound-active columns (e.g. UsersList "Name"
   * header is active when EITHER `orderBy === 'name'` OR `orderBy === 'lastName'`).
   * When provided, replaces the default `column === current` equality check
   * for both `sortDirection` and `active`. The visible click target still
   * dispatches `onSort(column)` — `column` remains the canonical key for the
   * click outcome, only the highlight rule expands.
   */
  activeWhen?: (current: T) => boolean;
}

/**
 * Standard sortable column header cell. Wraps the `<TableCell sortDirection>`
 * + `<TableSortLabel active direction onClick>` 9-line block that 14 list
 * pages each redeclare verbatim per sortable column (5–8 cells per page).
 *
 * The `current === column` check feeds both `sortDirection` and `active` so
 * the column shows the active sort indicator only when it owns the sort.
 *
 * Pairs with `nextSortDir` (slice 899) — the parent's `handleSort` callback
 * computes the next direction and updates URL state; this component just
 * surfaces the click.
 */
export function SortableHeaderCell<T extends string>({
  column,
  current,
  direction,
  onSort,
  children,
  sx,
  align,
  activeWhen,
}: SortableHeaderCellProps<T>) {
  const active = activeWhen ? activeWhen(current) : column === current;
  return (
    <TableCell sortDirection={active ? direction : false} sx={sx} align={align}>
      <TableSortLabel
        active={active}
        direction={active ? direction : 'asc'}
        onClick={() => onSort(column)}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );
}
